import React, { useState, useEffect } from 'react';
import api from '../api/axiosInstance';
import { Gear, FloppyDisk, Plus, PencilSimple, Trash, SpinnerGap, Info } from '@phosphor-icons/react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SettingsPage = () => {
  const [config, setConfig]           = useState(null);
  const [channels, setChannels]       = useState([]);
  const [formData, setFormData]       = useState({});
  const [nettingConfig, setNettingConfig] = useState({});  // {channelId: {CURRENT: bool}}
  const [newChannel, setNewChannel]   = useState({ name: '', code: '' });
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [deletingChannel, setDeletingChannel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSimActive, setIsSimActive] = useState(false);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [configRes, channelsRes, statsRes] = await Promise.allSettled([
        api.get(`${API}/config`),
        api.get(`${API}/channels`),
        api.get(`${API}/statistics`),
      ]);
      if (configRes.status === 'fulfilled') {
        setConfig(configRes.value.data);
        setFormData(configRes.value.data);
        const stored = configRes.value.data.netting_config || {};
        if (channelsRes.status === 'fulfilled') {
          const nc = {};
          channelsRes.value.data.forEach(ch => {
            const queues = stored[ch.id] || ['CURRENT'];
            nc[ch.id] = { CURRENT: queues.includes('CURRENT') || queues.includes('INBOUND') };
          });
          setNettingConfig(nc);
        }
      }
      if (channelsRes.status === 'fulfilled') setChannels(channelsRes.value.data);
      if (statsRes.status === 'fulfilled') setIsSimActive(statsRes.value.data.is_simulation_initialized || statsRes.value.data.is_simulation_active);
    } catch (error) { console.error('Error fetching data:', error); }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      // Build netting_config — CURRENT only
      const builtNettingConfig = {};
      Object.entries(nettingConfig).forEach(([chId, val]) => {
        builtNettingConfig[chId] = val.CURRENT ? ['CURRENT'] : [];
      });
      await api.put(`${API}/config`, {
        payments_per_hour:  parseInt(formData.payments_per_hour, 10),
        min_payment_value:  parseFloat(formData.min_payment_value),
        max_payment_value:  parseFloat(formData.max_payment_value),
        selected_algorithm: formData.selected_algorithm,
        overnight_balance:  parseFloat(formData.overnight_balance),
        central_bank_rate:  parseFloat(formData.central_bank_rate),
        money_market_rate:  parseFloat(formData.money_market_rate),
        netting_config:     builtNettingConfig,
        netting_threshold:  parseInt(formData.netting_threshold, 10) || 50,
        num_banks:          parseInt(formData.num_banks, 10) || 5,
      });
      toast.success('Configuration saved');
      fetchData();
    } catch (error) { toast.error('Failed to save configuration'); }
    finally { setSaving(false); }
  };

  const handleAddChannel = async () => {
    if (!newChannel.name || !newChannel.code) { toast.error('Please fill in all fields'); return; }
    try {
      await api.post(`${API}/channels`, newChannel);
      toast.success('Channel added');
      setNewChannel({ name: '', code: '' });
      setShowAddChannel(false);
      fetchData();
    } catch (error) { toast.error('Failed to add channel'); }
  };

  const handleRenameChannel = async (id, name) => {
    if (!name.trim()) { toast.error('Name cannot be empty'); return; }
    try {
      await api.patch(`${API}/channels/${id}`, { name: name.trim() });
      toast.success('Channel renamed');
      setEditingChannel(null);
      fetchData();
    } catch (e) { toast.error('Failed to rename channel'); }
  };

  const handleDeleteChannel = async (id) => {
    try {
      await api.delete(`${API}/channels/${id}`);
      toast.success('Channel deleted');
      setDeletingChannel(null);
      fetchData();
    } catch (e) { toast.error('Failed to delete channel'); }
  };

  const toggleNettingQueue = (channelId, queue) => {
    setNettingConfig(prev => ({
      ...prev,
      [channelId]: { ...(prev[channelId] || { CURRENT: true }), [queue]: !prev[channelId]?.[queue] }
    }));
  };



  if (!config) {
    return <div className="text-gray-600">Loading...</div>;
  }

  return (
    <div className="space-y-3 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black tracking-tight text-gray-900">
          Settings
        </h1>
        <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-medium">
          Configure simulation parameters
        </p>
      </div>

      {/* Quick Guide */}
      <Card className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-lg" data-testid="settings-guide">
        <div className="flex items-start gap-2.5">
          <Info size={16} weight="bold" className="text-indigo-500 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600 space-y-1">
            <p className="font-semibold text-gray-800">Quick Guide</p>
            <p><span className="font-semibold text-gray-700">Dashboard</span> — Initialize, start & stop simulations. Monitor KPIs and liquidity in real time.</p>
            <p><span className="font-semibold text-gray-700">Channels</span> — View per-channel balances, pending queues, and auto-resolved deficit notifications.</p>
            <p><span className="font-semibold text-gray-700">Payments</span> — Browse the full transaction log with filters and pagination.</p>
            <p><span className="font-semibold text-gray-700">Netting</span> — Inspect the bilateral netting matrix and bank net positions.</p>
            <p><span className="font-semibold text-gray-700">Settings</span> — Tune algorithm, payment generation, rates, channels & netting exclusions below.</p>
          </div>
        </div>
      </Card>

      {/* Algorithm Selection */}
      <Card className="p-2.5 bg-white border border-gray-200/50 rounded-lg" data-testid="algorithm-settings">
        <div className="space-y-2">
          <div className="flex items-center gap-2.5 mb-4">
            <Gear size={24} weight="duotone" className="text-indigo-500" />
            <h2 className="text-xs font-semibold text-gray-900">Liquidity Management Algorithm</h2>
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Selected Algorithm</Label>
            <Select 
              value={formData.selected_algorithm} 
              onValueChange={(value) => !isSimActive && setFormData({...formData, selected_algorithm: value})}
              disabled={isSimActive}
            >
              <SelectTrigger className={`bg-gray-50 border-gray-200 h-12 ${isSimActive ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="algorithm-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MULTILATERAL_NETTING">Multilateral Netting (Default)</SelectItem>
                <SelectItem value="PRIORITY_BASED">Priority-Based</SelectItem>
                <SelectItem value="TIME_OPTIMIZED">Time-Optimized</SelectItem>
                <SelectItem value="LIQUIDITY_OPTIMIZED">Liquidity-Optimized</SelectItem>
                <SelectItem value="HYBRID_SMART">Hybrid Smart Algorithm</SelectItem>
              </SelectContent>
            </Select>
            {isSimActive && (
              <p className="text-xs text-amber-600 mt-1 font-medium">Algorithm is locked after initialization. Reset the simulation to change.</p>
            )}
          </div>

          <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-1">
            <p className="font-semibold text-gray-800">Algorithm Descriptions</p>
            <p><span className="font-semibold text-gray-700">Multilateral Netting</span> — Default. Offsets payment obligations across all banks before processing, minimising gross flows and liquidity needs.</p>
            <p><span className="font-semibold text-gray-700">Priority-Based</span> — Processes payments based on priority level (1-5), higher priority first.</p>
            <p><span className="font-semibold text-gray-700">Time-Optimized</span> — Balances delivery deadlines and priority to minimize delays.</p>
            <p><span className="font-semibold text-gray-700">Liquidity-Optimized</span> — Processes credits first to increase liquidity, then debits by amount.</p>
            <p><span className="font-semibold text-gray-700">Hybrid Smart</span> — Advanced algorithm combining priority, urgency, and liquidity optimization for best overall performance.</p>
          </div>
        </div>
      </Card>

      {/* Payment Generation Settings */}
      <Card className="p-2.5 bg-white border border-gray-200/50 rounded-lg" data-testid="payment-settings">
        <h2 className="text-xs font-semibold text-gray-900 mb-4">Payment Generation Settings</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Payments Per Hour</Label>
            <Input
              type="number"
              value={formData.payments_per_hour}
              onChange={(e) => setFormData({...formData, payments_per_hour: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="payments-per-hour-input"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Min Payment Value ($)</Label>
            <Input
              type="number"
              value={formData.min_payment_value}
              onChange={(e) => setFormData({...formData, min_payment_value: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="min-payment-input"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Max Payment Value ($)</Label>
            <Input
              type="number"
              value={formData.max_payment_value}
              onChange={(e) => setFormData({...formData, max_payment_value: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="max-payment-input"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Overnight Balance ($)</Label>
            <Input
              type="number"
              value={formData.overnight_balance}
              onChange={(e) => setFormData({...formData, overnight_balance: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="overnight-balance-input"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Number of Banks (A–Z)</Label>
            <Input
              type="number"
              min="2"
              max="26"
              value={formData.num_banks || 5}
              onChange={(e) => setFormData({...formData, num_banks: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="num-banks-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Banks named A–{String.fromCharCode(64 + Math.min(26, Math.max(2, parseInt(formData.num_banks, 10) || 5)))}
            </p>
          </div>
        </div>
      </Card>

      {/* Interest Rates */}
      <Card className="p-2.5 bg-white border border-gray-200/50 rounded-lg" data-testid="rates-settings">
        <h2 className="text-xs font-semibold text-gray-900 mb-4">Interest Rates</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Central Bank Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.central_bank_rate}
              onChange={(e) => setFormData({...formData, central_bank_rate: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="central-bank-rate-input"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Money Market Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.money_market_rate}
              onChange={(e) => setFormData({...formData, money_market_rate: e.target.value})}
              className="bg-gray-50 border-gray-200 h-12 font-mono"
              data-testid="money-market-rate-input"
            />
          </div>
        </div>
      </Card>

      {/* Payment Channels — rename / delete */}
      <Card className="p-4 bg-white border border-gray-200 rounded-lg" data-testid="channels-settings">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900">Payment Channels</h2>
          <Button onClick={() => setShowAddChannel(!showAddChannel)} variant="outline" className="h-8 px-3 text-xs border-gray-200" data-testid="add-channel-btn">
            <Plus size={13} weight="bold" className="mr-1.5" /> Add Channel
          </Button>
        </div>

        {showAddChannel && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Channel Name</Label>
                <Input placeholder="e.g., SEPA" value={newChannel.name} onChange={e => setNewChannel({...newChannel, name: e.target.value})} className="h-9 text-sm bg-white border-gray-200" data-testid="new-channel-name" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Channel Code</Label>
                <Input placeholder="e.g., SEPA" value={newChannel.code} onChange={e => setNewChannel({...newChannel, code: e.target.value.toUpperCase()})} className="h-9 text-sm bg-white border-gray-200" data-testid="new-channel-code" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddChannel} className="h-8 px-4 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="save-new-channel">Save</Button>
              <Button onClick={() => { setShowAddChannel(false); setNewChannel({ name: '', code: '' }); }} variant="outline" className="h-8 px-4 text-xs">Cancel</Button>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {channels.map(ch => (
            <div key={ch.id} className="py-3 flex items-center gap-3" data-testid={`channel-${ch.code}`}>
              {editingChannel?.id === ch.id ? (
                <>
                  <Input
                    value={editingChannel.name}
                    onChange={e => setEditingChannel({ ...editingChannel, name: e.target.value })}
                    className="h-8 text-sm flex-1"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleRenameChannel(ch.id, editingChannel.name); if (e.key === 'Escape') setEditingChannel(null); }}
                  />
                  <Button onClick={() => handleRenameChannel(ch.id, editingChannel.name)} className="h-8 px-3 text-xs bg-indigo-600 hover:bg-indigo-700">Save</Button>
                  <Button onClick={() => setEditingChannel(null)} variant="outline" className="h-8 px-3 text-xs">Cancel</Button>
                </>
              ) : (
                <>
                  <span className="text-xs font-mono font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded px-2 py-0.5">{ch.code}</span>
                  <span className="text-sm font-semibold text-gray-900 flex-1">{ch.name}</span>
                  <span className="text-xs text-emerald-600 font-semibold">Active</span>
                  <button onClick={() => setEditingChannel({ id: ch.id, name: ch.name })} className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-indigo-600 transition-colors" data-testid={`rename-channel-${ch.code}`}>
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button onClick={() => setDeletingChannel(ch)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-channel-${ch.code}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Delete confirmation */}
        {deletingChannel && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-semibold text-red-800 mb-2">Delete <span className="font-black">{deletingChannel.name}</span>?</p>
            <p className="text-xs text-red-600 mb-3">This will permanently delete the channel, all its queues, accounts, and payments.</p>
            <div className="flex gap-2">
              <Button onClick={() => handleDeleteChannel(deletingChannel.id)} className="h-8 px-4 text-xs bg-red-600 hover:bg-red-700" data-testid="confirm-delete-channel">Confirm Delete</Button>
              <Button onClick={() => setDeletingChannel(null)} variant="outline" className="h-8 px-4 text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Netting Configuration — only visible for Multilateral Netting algorithm */}
      {formData.selected_algorithm === 'MULTILATERAL_NETTING' && (
      <Card className="p-4 bg-white border border-gray-200 rounded-lg" data-testid="netting-config-settings">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Netting Configuration</h2>
        <p className="text-xs text-gray-500 mb-4">Exclude specific channels from the multilateral netting process</p>

        <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">Netting Threshold</label>
          <Input
            type="number"
            min={10}
            max={500}
            value={formData.netting_threshold || 50}
            onChange={e => setFormData(f => ({ ...f, netting_threshold: e.target.value }))}
            className="h-8 w-24 text-xs font-mono"
            data-testid="netting-threshold-input"
          />
          <p className="text-[10px] text-gray-400">Netting triggers when pending payments exceed this count. Below threshold, individual parallel processing is used.</p>
        </div>

        <div className="overflow-hidden rounded-md border border-gray-200">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-gray-500 uppercase tracking-wide font-semibold">Channel</th>
                <th className="px-4 py-2.5 text-center text-gray-500 uppercase tracking-wide font-semibold">Include in Netting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {channels.map(ch => (
                <tr key={ch.id} className="hover:bg-gray-50" data-testid={`netting-config-row-${ch.code}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-gray-700">{ch.code}</span>
                    <span className="text-gray-500 ml-2">{ch.name}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={nettingConfig[ch.id]?.CURRENT ?? true}
                      onChange={() => toggleNettingQueue(ch.id, 'CURRENT')}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                      data-testid={`netting-${ch.code}-CURRENT`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-start">
        <Button
          onClick={handleSaveConfig}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 h-10 px-8 font-semibold text-sm flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          {saving
            ? <SpinnerGap size={16} weight="bold" className="animate-spin" />
            : <FloppyDisk size={16} weight="fill" />
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
};
